import React from 'react';
import { Button, Header5, InlineText3, Paragraph3, Tooltip } from '@cian/ui-kit';
import { ContentRow } from '../../../../components/ContentRow';
import { useOfferContext } from '../../../../context';
import { IPhone } from '../../../../types/offer';
import { useApplicationContext } from '../../../../../../../shared/serp/containers/ApplicationContext';
import { getDynamicCalltrackingPhone } from '../../../../api/dynamicCalltracking';

const EMPTY_PHONES = [{ countryCode: null, number: null }];

export function AgentPhoneButtonContainer() {
  const {
    offer,
    queryString,
    tracking,
    agentWorkTime,
    agentPhoneWarningMessage,
    isDealRentPrepaymentExperimentActive,
  } = useOfferContext();
  const { logger, httpApi } = useApplicationContext();

  const [opened, setOpened] = React.useState(false);
  // dynamic calltracking
  const [comagicLoaded, setComagicLoaded] = React.useState(false);
  const [comagicLoading, setComagicLoading] = React.useState(false);
  const [comagicPhone, setComagicPhone] = React.useState<IPhone | null>(null);

  const phones: IPhone[] = React.useMemo(() => {
    if (!comagicLoaded || !offer.phones) {
      return EMPTY_PHONES;
    }

    return comagicPhone ? [comagicPhone] : offer.phones;
  }, [comagicLoaded, comagicPhone, offer.phones]);

  const stopPropagation = (e: React.SyntheticEvent<HTMLElement>) => e.stopPropagation();

  const handleClick = React.useCallback(
    async (e: React.MouseEvent<HTMLElement>) => {
      stopPropagation(e);
      tracking.trackPhoneOpened(true);
      const { newbuildingDynamicCalltracking, id } = offer;
      const siteBlockId = newbuildingDynamicCalltracking && newbuildingDynamicCalltracking.siteBlockId;
      setComagicLoading(true);
      if (siteBlockId) {
        try {
          const { phone } = await getDynamicCalltrackingPhone({
            httpApi,
            logger,
            blockId: siteBlockId,
            announcementId: id,
            placeType: 'offer',
            dynamicCalltrackingPlaceType: 'sidePanel',
            queryString,
          });

          if (phone) {
            setComagicPhone({
              countryCode: phone[0],
              number: phone.slice(1),
            });
          }
        } catch (err) {}
      }
      setComagicLoading(false);
      setComagicLoaded(true);
      setOpened(true);
    },
    [offer, queryString, httpApi, logger, tracking],
  );

  const slicedPhone = React.useMemo(() => {
    return offer.phones && offer.phones[0] ? `${formatPhone(offer.phones[0]).slice(0, -2)}...` : '';
  }, [offer.phones]);

  if (isDealRentPrepaymentExperimentActive) {
    return null;
  }

  if (!opened) {
    return (
      <Tooltip placement="left" title="Показать телефон">
        <Button data-mark="PhoneButton" size="XS" onClick={handleClick} theme="fill_primary" loading={comagicLoading}>
          {slicedPhone}
        </Button>
      </Tooltip>
    );
  }

  return (
    <>
      <ContentRow>
        {phones.map((phone, index) => (
          <ContentRow>
            <Header5 data-mark="PhoneValue" onClick={stopPropagation}>
              {formatPhone(phone)}
            </Header5>
            {index === phones.length - 1 && agentWorkTime && (
              <Paragraph3 color="gray60_100">{agentWorkTime}</Paragraph3>
            )}
          </ContentRow>
        ))}
      </ContentRow>
      {agentPhoneWarningMessage && (
        <ContentRow>
          <InlineText3 data-mark="new-agent-warning" color="gray60_100" whiteSpace="pre-line">
            {agentPhoneWarningMessage}
          </InlineText3>
        </ContentRow>
      )}
    </>
  );
}

export function formatPhone(phone: IPhone): string {
  if (!phone || !phone.number) {
    return '';
  }

  return (
    `${phone.countryCode && phone.countryCode.startsWith('+') ? '' : '+'}${phone.countryCode} ` +
    `${phone.number.substr(0, 3)} ` +
    `${phone.number.substr(3, 3)}-${phone.number.substr(6, 2)}-${phone.number.substr(8, 2)}`
  );
}
